/**
 * Supsis Web Analytics tracker.
 * Socket kullanmaz; event'ler batch halinde HTTP ile gonderilir
 * (beta/prod: API GW + Lambda + Kafka, local: tablet public collect endpoint).
 */
import log2 from "../../js/log2-lite";
import trackerConfig from "./config";
import identity, { uuid } from "./identity";
import transport from "./transport";
import isBot from "./botDetect";
import { AUTO_EVENTS, IDENTITY_EVENTS, normalizeEventName, resolveCategory, CATEGORIES } from "./events";
import PageViewCollector from "./collectors/pageView";
import ClicksCollector from "./collectors/clicks";
import FormsCollector from "./collectors/forms";
import SiteSearchCollector from "./collectors/siteSearch";
import MediaCollector from "./collectors/media";
import ElementViewCollector from "./collectors/elementView";
import ErrorsCollector from "./collectors/errors";
import VitalsCollector from "./collectors/vitals";
import CopyCollector from "./collectors/copy";
import rulesEngine from "./rules/engine";
import loadRuleset from "./rules/loader";
import pathMatcher from "./rules/paths";
import PageReporter from "./pages";
import { resolvePageKey } from "./pages/pageKey";

const log = log2("TRACKER");

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

function readUtm() {
    const utm = {};
    try {
        const params = new URLSearchParams(window.location.search);
        for (let i = 0; i < UTM_KEYS.length; i++) {
            const value = params.get(UTM_KEYS[i]);
            if (value) utm[UTM_KEYS[i].replace("utm_", "")] = String(value).slice(0, 120);
        }
        const gclid = params.get("gclid");
        const fbclid = params.get("fbclid");
        if (gclid) utm.gclid = String(gclid).slice(0, 200);
        if (fbclid) utm.fbclid = String(fbclid).slice(0, 200);
    } catch (e) {
        /* yoksay */
    }
    return utm;
}

function deviceType() {
    const ua = navigator.userAgent || "";
    if (/iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return "tablet";
    if (/Mobile|iPhone|iPod|Android|BlackBerry|Opera Mini|IEMobile/i.test(ua)) return "mobile";
    return "desktop";
}

function timezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch (e) {
        return "";
    }
}

class SupsisTracker {
    constructor() {
        this.started = false;
        this.siteId = null;
        this.contactId = null;
        this.pageViewCollector = null;
        this.siteSearchCollector = null;
        this.traits = {};
        this.returningVisitor = false;
    }

    /**
     * @param {Object} options { siteId, endpoint, settings }
     */
    init(options) {
        if (this.started) {
            log.debug("tracker already started");
            return;
        }
        const opts = options || {};
        if (!opts.siteId || !opts.endpoint) {
            log.warn("tracker init skipped, missing siteId or endpoint");
            return;
        }

        trackerConfig.apply(opts.settings);
        trackerConfig.siteId = opts.siteId;
        trackerConfig.endpoint = opts.endpoint;

        if (trackerConfig.isDoNotTrack()) {
            log.info("tracker disabled by Do Not Track");
            return;
        }
        if (isBot()) {
            log.info("tracker disabled for bot traffic");
            return;
        }
        if (trackerConfig.isExcludedPath()) {
            log.info("tracker disabled for excluded path");
            return;
        }

        this.siteId = opts.siteId;
        this.started = true;
        identity.configure(trackerConfig.sessionTimeoutMinutes);

        const self = this;
        // siteId query'de de tasinir: tablet getSites middleware'i header yoksa query.siteId okur
        const endpoint = opts.endpoint + (opts.endpoint.indexOf("?") === -1 ? "?" : "&") + "siteId=" + opts.siteId;
        transport.init(endpoint, function () {
            return self.context();
        });

        // Sayfa kayit defteri adresleri; verilmezse sayfa raporlama calismaz
        this.pageEndpoint = opts.pageEndpoint || null;
        this.pageStatusUrl = opts.pageStatusUrl || null;

        this.startCollectors();
        this.startRules(opts.settings);
        this.startPageReporter();
        log.info("tracker started", { siteId: this.siteId, autocapture: trackerConfig.autocapture });
        // SDK kuyrugunda bekleyen track/identify cagrilari bu event ile calisir
        try {
            window.dispatchEvent(new Event("supsis-analytics-ready"));
        } catch (e) {
            /* eski tarayicilar: kuyruk sonraki komutlarla bosalir */
        }
    }

    /** SDK analytics komutu; ayardaki collector'lari yalnizca kapatabilir. */
    overrideConfig(payload) {
        trackerConfig.override(payload);
    }

    /**
     * Panelde tanimlanan custom event kurallarini indirip motoru baslatir.
     * Kural yoksa (surum 0) hicbir istek atilmaz.
     */
    startRules(settings) {
        const version = settings && settings.eventRulesVersion;
        rulesEngine.attach(this, this.siteId);

        loadRuleset(this.siteId, version)
            .then(function (ruleset) {
                if (!ruleset) return;
                // Editor "sitede test et" modunu actiysa yayindaki kural seti uzerine yazilmaz
                if (rulesEngine.isTestMode()) return;
                rulesEngine.configure(ruleset);
                rulesEngine.start();
            })
            .catch(function (e) {
                log.debug("rules init failed", e);
            });
    }

    startCollectors() {
        const isReturning = identity.isReturning();
        this.returningVisitor = isReturning;

        this.pageViewCollector = new PageViewCollector(this);
        this.siteSearchCollector = new SiteSearchCollector(this);

        // session_start, page_view'dan once gitmeli: sunucu oturum kaydini buradan acar
        identity.sid();
        if (identity.isNewSession) {
            this.emit(AUTO_EVENTS.SESSION_START, {
                is_returning: isReturning,
                device_type: deviceType(),
            });
        }

        this.pageViewCollector.start();
        if (trackerConfig.isEnabled("siteSearch")) this.siteSearchCollector.start();
        if (trackerConfig.isEnabled("forms")) new FormsCollector(this).start();
        if (trackerConfig.isEnabled("video")) new MediaCollector(this).start();
        if (trackerConfig.isEnabled("elementViewed")) new ElementViewCollector(this).start();
        if (trackerConfig.isEnabled("jsError")) new ErrorsCollector(this).start();
        if (trackerConfig.isEnabled("webVitals")) new VitalsCollector(this).start();
        if (trackerConfig.isEnabled("copy")) new CopyCollector(this).start();

        // click / outbound_click / file_download / rage_click / dead_click tek dinleyicide
        new ClicksCollector(this).start();
    }

    /** Her batch'in basinda gonderilen ortam bilgisi. */
    context() {
        return {
            siteId: this.siteId,
            uid: identity.uid(),
            sid: identity.sid(),
            contactId: this.contactId || undefined,
            ctx: {
                url: String(window.location.href).slice(0, 1000),
                path: window.location.pathname,
                host: window.location.hostname,
                title: String(document.title || "").slice(0, 300),
                referrer: String(document.referrer || "").slice(0, 1000),
                lang: navigator.language || "",
                tz: timezone(),
                screen: (window.screen ? window.screen.width + "x" + window.screen.height : ""),
                viewport: window.innerWidth + "x" + window.innerHeight,
                deviceType: deviceType(),
                utm: readUtm(),
            },
        };
    }

    /**
     * Event kuyruga eklenir.
     * @param {string} name event ismi
     * @param {Object} props serbest property'ler (rezerve isimler sunucuda normalize edilir)
     * @param {Object} fields WebEvent ust seviye alanlari (duration, click, form, search ...)
     */
    emit(name, props, fields) {
        if (!this.started) return;
        const normalized = normalizeEventName(name);
        if (!normalized) return;
        if (trackerConfig.isExcludedPath()) return;

        identity.touch();

        const page = {
            url: String(window.location.href).slice(0, 1000),
            path: window.location.pathname,
            host: window.location.hostname,
            title: String(document.title || "").slice(0, 300),
            query: String(window.location.search || "").slice(0, 500),
        };
        // Kural setindeki sayfa takma adi (/urun/123 -> /urun/:id).
        // Takma ad tanimli degilse batch'i sisirmemek icin alan eklenmez.
        const pattern = pathMatcher.resolvePattern();
        if (pattern) page.pattern = pattern;

        // Sayfa kayit defteri anahtari; sunucu sayfa tipi/urun eslemesini buradan yapar
        const pageKey = this.currentPageKey();
        if (pageKey) page.key = pageKey;

        const event = Object.assign({}, fields || {}, {
            id: uuid(),
            name: normalized,
            category: resolveCategory(normalized),
            // Kural motoru async property cozerken gecen sureyi degil tetiklenme anini yazar
            ts: (fields && fields.ts) || Date.now(),
            props: props || {},
            page: page,
        });

        transport.enqueue(event);
    }

    /**
     * SDK track komutu.
     * Ayni isimde `sdk` tetikleyicili kural varsa property'ler DOM'dan zenginlestirilir;
     * bu cozumleme async oldugu icin event tum degerler hazir olunca gonderilir.
     */
    track(name, props) {
        const normalized = normalizeEventName(name);
        if (!rulesEngine.hasSdkRule(normalized)) {
            this.emit(name, props);
            return;
        }

        const self = this;
        const triggeredAt = Date.now();
        rulesEngine
            .enrichSdkProps(normalized, props)
            .then(function (merged) {
                self.emit(name, merged, { ts: triggeredAt });
            })
            .catch(function () {
                self.emit(name, props, { ts: triggeredAt });
            });
    }

    /** Kural setindeki js govdelerine verilen ziyaretci bilgisi. */
    visitorInfo() {
        return {
            uid: identity.uid(),
            sid: identity.sid(),
            contactId: this.contactId || null,
            traits: this.traits || {},
            isNewSession: identity.isNewSession,
            isReturning: !!this.returningVisitor,
        };
    }

    /** Widget kaynakli event (chat_widget_opened, proactive_trigger_*). */
    trackWidget(name, props) {
        const merged = Object.assign({ source: "widget" }, props || {});
        this.emit(name, merged);
    }

    /** Kimlik bilgileri; contactId gelince WebVisitor ile Visitor profili eslenir. */
    identify(traits) {
        if (!traits || typeof traits !== "object") return;
        this.traits = Object.assign({}, this.traits, traits);
        if (traits.contactId) this.contactId = traits.contactId;
        this.emit(IDENTITY_EVENTS.IDENTIFY, {
            user_id: traits.userId || traits.user_id || undefined,
            email: traits.email || undefined,
            phone: traits.phone || undefined,
            name: traits.name || traits.fullname || undefined,
            contact_id: traits.contactId || undefined,
        });
        this.flush("identify");
    }

    alias(id) {
        if (!id) return;
        this.emit(IDENTITY_EVENTS.ALIAS, { user_id: String(id).slice(0, 120) });
    }

    group(payload) {
        if (!payload || !payload.groupId) return;
        this.emit(IDENTITY_EVENTS.GROUP, {
            group_id: String(payload.groupId).slice(0, 120),
            traits: payload.traits || {},
        });
    }

    consent(payload) {
        const analytics = !(payload && payload.analytics === false);
        this.emit(IDENTITY_EVENTS.CONSENT_UPDATED, { analytics: analytics });
        this.flush("consent");
        if (!analytics) {
            transport.stop();
            this.started = false;
        }
    }

    reset() {
        this.emit(IDENTITY_EVENTS.RESET, {});
        this.flush("reset");
        identity.reset();
        this.contactId = null;
        this.traits = {};
    }

    /** SPA gecislerinde arama parametresini ve sayfa bazli kurallari yeniden kontrol eder. */
    onPageChanged() {
        if (this.siteSearchCollector && trackerConfig.isEnabled("siteSearch")) {
            this.siteSearchCollector.checkUrl();
        }
        rulesEngine.onPageView();
        this.pageKeyCache = null;
        if (this.pageReporter) this.pageReporter.schedule();
    }

    /**
     * Gecerli sayfanin anahtarini (pk) doner; sayfa basina bir kez hesaplanir.
     * @returns {String} pk veya bos string
     */
    currentPageKey() {
        const href = window.location.href;
        if (this.pageKeyCache && this.pageKeyCache.href === href) return this.pageKeyCache.pk;

        const pages = trackerConfig.pages || {};
        const resolved = resolvePageKey(pages.keepQueryParams || []);
        const pk = resolved ? resolved.pk : "";
        this.pageKeyCache = { href: href, pk: pk };
        return pk;
    }

    /** Sayfa kayit defteri: meta toplama aciksa ilk page_view'dan sonra calisir. */
    startPageReporter() {
        const pages = trackerConfig.pages || {};
        if (pages.collectMeta === false) return;
        if (!this.pageEndpoint || !this.pageStatusUrl) return;

        this.pageReporter = new PageReporter(this);
        this.pageReporter.schedule();
    }

    flush(reason) {
        transport.flush(reason || "manual");
    }

    isStarted() {
        return this.started;
    }
}

export const tracker = new SupsisTracker();
export { CATEGORIES };
export default tracker;



//////////////////
// WEBPACK FOOTER
// ./static/src/tracker/index.js
// module id = null
// module chunks = 